import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AvailableLanguages } from '@constants';
import { DeviceInfoService } from '@core/services/device-info.service';
import { environment } from '@environment';
import { DeviceType } from '@interfaces';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { isEmbeddedBrowser, isSafariBrowser } from '@core/services/browser-detection';

type PrivacyPolicyUrls = Partial<Record<AvailableLanguages, string>>;

@Component({
  selector: 'app-privacy-policy',
  templateUrl: './privacy-policy.component.html',
  styleUrls: ['./privacy-policy.component.scss'],
  standalone: false,
})
export class PrivacyPolicyComponent implements OnInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly location = inject(Location);

  public pdfUrl = '';
  public safePdfUrl: SafeResourceUrl | null = null;
  public shouldRenderInlineViewer = false;
  public openLinkTarget = '_blank';

  private readonly privacyPolicyUrls: PrivacyPolicyUrls = environment.PRIVACY_POLICY_PDFS || {};
  private langChangeSubscription?: Subscription;
  private deviceInfoSubscription?: Subscription;

  public ngOnInit(): void {
    this.updatePdfUrl(this.getActiveLang());
    this.updateViewerMode();

    this.langChangeSubscription = this.translateService.onLangChange.subscribe(({ lang }) => {
      this.updatePdfUrl(lang as AvailableLanguages);
    });

    this.deviceInfoSubscription = this.deviceInfoService.infoEmitter.subscribe(() => {
      this.updateViewerMode();
    });
  }

  public ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
    this.deviceInfoSubscription?.unsubscribe();
  }

  public goBack(): void {
    this.location.back();
  }

  private getActiveLang(): AvailableLanguages {
    const activeLang = (this.translateService.currentLang ||
      this.translateService.defaultLang ||
      'en') as AvailableLanguages;

    return activeLang;
  }

  private updatePdfUrl(lang: AvailableLanguages): void {
    const resolvedPdfUrl = this.privacyPolicyUrls[lang] || this.privacyPolicyUrls.en || '';

    this.pdfUrl = resolvedPdfUrl;
    this.safePdfUrl = resolvedPdfUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(resolvedPdfUrl) : null;
    this.updateViewerMode();
  }

  private updateViewerMode(): void {
    const deviceInfo = this.deviceInfoService.getInfo();
    const isMobileDevice = deviceInfo.deviceTypeDetected !== DeviceType.DESKTOP;
    const userAgent = deviceInfo.deviceInfo.userAgent || window.navigator.userAgent || '';
    const browserName = deviceInfo.deviceInfo.browser || '';
    const shouldPreferDirectOpen =
      isMobileDevice || isEmbeddedBrowser(userAgent) || isSafariBrowser(browserName, userAgent);

    this.shouldRenderInlineViewer = !!this.safePdfUrl && !shouldPreferDirectOpen;
    this.openLinkTarget = shouldPreferDirectOpen ? '_self' : '_blank';
  }
}
